import { Injectable } from '@angular/core';
import { CameraError, CameraErrorCode, mapCameraError } from '../model/camera-error';

export type CameraFacingMode = 'environment' | 'user';

@Injectable({
  providedIn: 'root',
})
export class CameraService {
  private stream: MediaStream | null = null;
  private currentFacingMode: CameraFacingMode = 'environment';

  isSupported(): boolean {
    return !!(navigator?.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function');
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  getCurrentFacingMode(): CameraFacingMode {
    return this.currentFacingMode;
  }

  async requestCamera(facingMode: CameraFacingMode = 'environment'): Promise<MediaStream> {
    if (!this.isSupported()) {
      const error: CameraError = {
        code: CameraErrorCode.NOT_SUPPORTED,
        message:
          'Es necesario contar con una cámara para realizar esta operación. Por favor permitir o instalar una cámara en este dispositivo o ingresar desde un dispositivo móvil.',
      };
      throw error;
    }

    this.stopCamera();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facingMode } },
        audio: false,
      });
      this.stream = stream;
      this.currentFacingMode = facingMode;
      return stream;
    } catch (error) {
      throw mapCameraError(error);
    }
  }

  async switchCamera(): Promise<MediaStream> {
    const nextFacingMode: CameraFacingMode = this.currentFacingMode === 'environment' ? 'user' : 'environment';
    return this.requestCamera(nextFacingMode);
  }

  async getVideoDevices(): Promise<MediaDeviceInfo[]> {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      return [];
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput');
  }

  hasTorch(): boolean {
    const track = this.stream?.getVideoTracks()[0];
    if (!track?.getSettings || !track.getCapabilities) {
      return false;
    }
    try {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
      return track.getSettings().facingMode === 'environment' && capabilities.torch === true;
    } catch {
      return false;
    }
  }

  async setTorch(enabled: boolean): Promise<boolean> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track?.applyConstraints || !this.hasTorch()) {
      return false;
    }
    try {
      await track.applyConstraints({ advanced: [{ torch: enabled } as MediaTrackConstraintSet] });
      return true;
    } catch {
      return false;
    }
  }

  stopCamera(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}
